import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Seo } from '../components/Seo';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/apiError';
import { analytics } from '../lib/analytics';
import Logo from '../components/Logo';
import { Mail, Lock, User, Loader2, Gift } from 'lucide-react';

const Register = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Pre-fill referral code from URL param
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
    }
  }, [searchParams]);

  // Carry the plan+billing interval chosen on the homepage pricing toggle through
  // signup, so Settings can pre-select the same interval instead of defaulting
  // back to monthly. Settings reads and clears this; no checkout is auto-triggered.
  const pendingPlan = searchParams.get('plan');
  const pendingBilling = searchParams.get('billing');

  // Password strength validation
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
  };
  const isPasswordStrong = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong) {
      toast.error('Password does not meet requirements');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password, referralCode || null);
      analytics.signUp('email');
      if ((pendingPlan === 'standard' || pendingPlan === 'pro') && (pendingBilling === 'year' || pendingBilling === 'month')) {
        localStorage.setItem('cc_pending_billing_interval', pendingBilling);
      }
      toast.success('Account created — check your inbox to verify your email.');
      navigate('/welcome');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Seo noindex />
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col">
        <header className="p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Logo />
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md border-0 shadow-none bg-transparent">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-3xl font-heading font-semibold">Create an account</CardTitle>
              <CardDescription>Start invoicing in seconds. No credit card required.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 h-12 rounded-xl"
                      required
                      data-testid="name-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 rounded-xl"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-12 rounded-xl"
                      required
                      data-testid="password-input"
                    />
                  </div>
                  <div className="space-y-1 mt-2">
                    <p className="text-xs text-muted-foreground font-medium">Password must contain:</p>
                    <ul className="text-xs space-y-0.5">
                      <li className={passwordChecks.length ? 'text-green-600' : 'text-muted-foreground'}>
                        {passwordChecks.length ? '✓' : '○'} At least 8 characters
                      </li>
                      <li className={passwordChecks.uppercase ? 'text-green-600' : 'text-muted-foreground'}>
                        {passwordChecks.uppercase ? '✓' : '○'} One uppercase letter
                      </li>
                      <li className={passwordChecks.lowercase ? 'text-green-600' : 'text-muted-foreground'}>
                        {passwordChecks.lowercase ? '✓' : '○'} One lowercase letter
                      </li>
                      <li className={passwordChecks.number ? 'text-green-600' : 'text-muted-foreground'}>
                        {passwordChecks.number ? '✓' : '○'} One number
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`pl-10 h-12 rounded-xl ${confirmPassword && password !== confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500">Passwords do not match</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="referralCode">Referral or ambassador code — optional</Label>
                  <div className="relative">
                    <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="referralCode"
                      type="text"
                      placeholder="e.g. ABC123"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      className="pl-10 h-12 rounded-xl uppercase"
                      maxLength={6}
                      data-testid="referral-code-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full h-12 rounded-full" disabled={loading} data-testid="register-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create account'}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-4">
                  By creating an account, you agree to our{' '}
                  <Link to="/terms" className="text-primary hover:underline" data-testid="terms-link">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-primary hover:underline" data-testid="privacy-link">Privacy Policy</Link>.
                </p>
              </form>
            </CardContent>
            <CardFooter className="flex justify-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
                  Log in
                </Link>
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>

      {/* Right side - Riso Blue panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-accent text-accent-foreground p-12 flex-col justify-end">
        <span className="font-mono text-[11px] uppercase tracking-[0.28em] opacity-80 mb-6 block">
          ✱ &nbsp; Sign up
        </span>
        <h2 className="font-heading font-semibold text-4xl mb-4">Join the creative community</h2>
        <p className="opacity-90 text-lg max-w-md">
          Photographers, designers, and artists trust CreateCollect to handle their invoicing so they can focus on their craft.
        </p>
      </div>
    </div>
  );
};

export default Register;
